// @flow
import {ofType} from 'redux-observable';
import {EMPTY} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {LoginSuccess, GetSessionSuccess} from '../auth/duck';
import {
  SaveGeneralContactSuccess,
  SaveGeneralInformationSuccess,
} from '../configure/general/duck';
import {SaveSocialMediasSuccess} from '../configure/social-medias/duck';
import {
  getContactApi,
  getContactTypesApi,
  getSocialApi,
  getSocialMediaTypesApi,
} from './api';

import type {AjaxError} from 'rxjs/ajax';
import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../store/utils';
import type {GeneralConfigAction} from '../configure/general/duck';
import type {
  Contact,
  ContactType,
  SocialMediaType,
  SocialMedia,
} from '../../entities';

export type InformationAction = Action & {
  contact?: Contact[],
  contactTypes?: ContactType[],
  socialMediaTypes?: SocialMediaType[],
  error?: string,
};

export type InformationState = {
  contact?: Contact[],
  isFetchingContact?: boolean,
  fetchContactError?: string,
  contactTypes?: ContactType[],
  socialMediaTypes?: SocialMediaType[],
  isFetchingSocial?: boolean,
  socialMedia?: SocialMedia[],
};

export class FetchInformation extends ActionCreator<InformationAction> {}
export class FetchInformationSuccess extends ActionCreator<InformationAction> {}
export class FetchInformationErrorFn extends ActionCreator<InformationAction> {}

export class FetchContact extends ActionCreator<InformationAction> {}
export class FetchContactSuccess extends ActionCreator<InformationAction> {}
export class FetchContactErrorFn extends ActionCreator<InformationAction> {}

export class FetchContactTypesSuccess extends ActionCreator<InformationAction> {}

export class FetchSocial extends ActionCreator<InformationAction> {}
export class FetchSocialSuccess extends ActionCreator<InformationAction> {}
export class FetchSocialErrorFn extends ActionCreator<InformationAction> {}

export class FetchSocialMediasSuccess extends ActionCreator<InformationAction> {}

export const actions = {
  fetchStoreContact: () => new FetchContact(),
};

export const initialState = {
  isFetchingContact: true,
  isFetchingSocial: true,
  isGettingScheduleShipment: true,
};

export default createReducer<InformationState>(initialState, {
  [SaveGeneralInformationSuccess.type]: (
    state,
    action: GeneralConfigAction
  ) => ({
    ...state,
    information: action.information,
  }),
  [FetchContact.type]: state => ({...state, isFetchingContact: true}),
  [FetchContactSuccess.type]: (state, action: InformationAction) => ({
    ...state,
    isFetchingContact: undefined,
    contact: action.contact,
  }),
  [FetchContactErrorFn.type]: (state, action: InformationAction) => ({
    ...state,
    error: action.error,
  }),
  [FetchContactTypesSuccess.type]: (state, action: InformationAction) => ({
    ...state,
    contactTypes: action.contactTypes,
  }),
  [FetchSocialMediasSuccess.type]: (state, action: InformationAction) => ({
    ...state,
    socialMediaTypes: action.socialMediaTypes,
  }),
  [FetchSocial.type]: state => ({...state, isFetchingSocial: true}),
  [FetchSocialSuccess.type]: (state, action: InformationAction) => ({
    ...state,
    isFetchingSocial: undefined,
    socialMedia: action.socialMedia,
  }),
  [FetchSocialErrorFn.type]: (state, action: InformationAction) => ({
    ...state,
    error: action.error,
  }),
});

export function fetchContactEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      FetchContact.type,
      GetSessionSuccess.type,
      LoginSuccess.type,
      SaveGeneralContactSuccess.type
    ),
    switchMap(() =>
      getContactApi().pipe(
        map((contact: Contact[]) => new FetchContactSuccess({contact})),
        buildCatchError(FetchContactErrorFn)
      )
    )
  );
}

export function fetchContactTypesEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(GetSessionSuccess.type, LoginSuccess.type),
    switchMap(() =>
      getContactTypesApi().pipe(
        map(
          (contactTypes: ContactType[]) =>
            new FetchContactTypesSuccess({contactTypes})
        ),
        catchError((error: AjaxError) => EMPTY)
      )
    )
  );
}

export function fetchSocialEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      FetchSocial.type,
      GetSessionSuccess.type,
      LoginSuccess.type,
      SaveSocialMediasSuccess.type
    ),
    switchMap(() =>
      getSocialApi().pipe(
        map(
          (socialMedia: SocialMedia[]) => new FetchSocialSuccess({socialMedia})
        ),
        buildCatchError(FetchSocialErrorFn)
      )
    )
  );
}

export function fetchSocialMediaTypesEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(GetSessionSuccess.type, LoginSuccess.type),
    switchMap(() =>
      getSocialMediaTypesApi().pipe(
        map(
          (socialMediaTypes: SocialMediaType[]) =>
            new FetchSocialMediasSuccess({socialMediaTypes})
        ),
        catchError((error: AjaxError) => EMPTY)
      )
    )
  );
}
